/**
 * powWorker.js — 前端轻量级 Web Worker 动态 PoW 算力求解引擎
 *
 * 1. 采用 Blob 动态加载 Web Worker，零构建器依赖、零外部资源网络开销、零 UI 线程卡顿
 * 2. 纯 32 位位运算 SHA-256 哈希引擎，哈希寻解速度达数十万次/秒
 * 3. 完美结合服务端自适应难度算法：平峰无感（<1ms），被攻击时以计算密集硬性挤兑黑产群控真机算力
 */

import request from '@/utils/request';

const WORKER_CODE = `
function rightRotate(value, amount) {
  return (value >>> amount) | (value << (32 - amount));
}
var mathPow = Math.pow;
var maxWord = mathPow(2, 32);
var lengthProperty = 'length';
var i, j;
var hashInit = [];
var kInit = [];
var primeCounter = 0;
var isComposite = {};
for (var candidate = 2; primeCounter < 64; candidate++) {
  if (!isComposite[candidate]) {
    for (i = 0; i < 313; i += candidate) {
      isComposite[i] = candidate;
    }
    hashInit[primeCounter] = (mathPow(candidate, 0.5) * maxWord) | 0;
    kInit[primeCounter++] = (mathPow(candidate, 1 / 3) * maxWord) | 0;
  }
}

function sha256(ascii) {
  var result = '';
  var words = [];
  var asciiBitLength = ascii[lengthProperty] * 8;
  var hash = hashInit.slice(0);
  var k = kInit;
  ascii += '\\x80';
  while ((ascii[lengthProperty] % 64) - 56) ascii += '\\x00';
  for (i = 0; i < ascii[lengthProperty]; i++) {
    j = ascii.charCodeAt(i);
    words[i >> 2] |= j << (((3 - i) % 4) * 8);
  }
  words[words[lengthProperty]] = (asciiBitLength / maxWord) | 0;
  words[words[lengthProperty]] = asciiBitLength | 0;
  for (j = 0; j < words[lengthProperty]; ) {
    var w = words.slice(j, (j += 16));
    var oldHash = hash;
    hash = hash.slice(0, 8);
    for (i = 0; i < 64; i++) {
      var w15 = w[i - 15], w2 = w[i - 2];
      var s0 = rightRotate(w15, 7) ^ rightRotate(w15, 18) ^ (w15 >>> 3);
      var s1 = rightRotate(w2, 17) ^ rightRotate(w2, 19) ^ (w2 >>> 10);
      w[i] = (i < 16) ? w[i] : (w[i - 16] + s0 + w[i - 7] + s1) | 0;
      var s1h = rightRotate(hash[4], 6) ^ rightRotate(hash[4], 11) ^ rightRotate(hash[4], 25);
      var ch = (hash[4] & hash[5]) ^ (~hash[4] & hash[6]);
      var temp1 = (hash[7] + s1h + ch + k[i] + w[i]) | 0;
      var s0h = rightRotate(hash[0], 2) ^ rightRotate(hash[0], 13) ^ rightRotate(hash[0], 22);
      var maj = (hash[0] & hash[1]) ^ (hash[0] & hash[2]) ^ (hash[1] & hash[2]);
      var temp2 = (s0h + maj) | 0;
      hash = [(temp1 + temp2) | 0].concat(hash);
      hash[4] = (hash[4] + temp1) | 0;
      hash.pop();
    }
    for (i = 0; i < 8; i++) {
      hash[i] = (hash[i] + oldHash[i]) | 0;
    }
  }
  for (i = 0; i < 8; i++) {
    for (j = 3; j >= 0; j--) {
      var b = (hash[i] >> (8 * j)) & 255;
      result += (b < 16 ? '0' : '') + b.toString(16);
    }
  }
  return result;
}

function hasLeadingZeroBits(hexStr, bits) {
  var hexZeros = Math.floor(bits / 4);
  var remBits = bits % 4;
  for (var i = 0; i < hexZeros; i++) {
    if (hexStr[i] !== '0') return false;
  }
  if (remBits > 0) {
    var val = parseInt(hexStr[hexZeros], 16);
    var maxAllowed = (1 << (4 - remBits)) - 1;
    if (val > maxAllowed) return false;
  }
  return true;
}

self.onmessage = function(e) {
  var salt = e.data.salt || '';
  var difficulty = e.data.difficulty || 8;
  var maxIters = e.data.maxIters || 5000000;
  var t0 = Date.now();

  for (var i = 0; i < maxIters; i++) {
    var nonce = i.toString();
    var hash = sha256(salt + nonce);
    if (hasLeadingZeroBits(hash, difficulty)) {
      self.postMessage({
        success: true,
        nonce: nonce,
        hash: hash,
        iters: i,
        durationMs: Date.now() - t0
      });
      return;
    }
  }
  self.postMessage({ success: false, reason: 'exceeded_max_iters' });
};
`;

/**
 * 求解指定 Salt 和难度难度的 PoW 挑战
 * @param {Object} options
 * @param {string} options.salt 挑战随机盐
 * @param {number} options.difficulty 难度（前导零 bit 数）
 * @param {number} [options.maxIters] 最大迭代次数
 * @param {number} [options.timeoutMs] 超时时间 (毫秒)
 * @returns {Promise<{success: boolean, nonce: string, iters: number, durationMs: number}>}
 */
export function solvePoW({ salt, difficulty, maxIters = 4000000, timeoutMs = 25000 }) {
  return new Promise((resolve) => {
    if (!salt || typeof difficulty !== 'number') {
      resolve({ success: false, reason: 'invalid_params' });
      return;
    }

    let worker = null;
    let workerUrl = null;
    let timer = null;

    const cleanup = () => {
      if (timer) clearTimeout(timer);
      if (worker) {
        worker.terminate();
        worker = null;
      }
      if (workerUrl && typeof URL !== 'undefined') {
        URL.revokeObjectURL(workerUrl);
        workerUrl = null;
      }
    };

    // 尝试创建 Web Worker
    try {
      if (typeof Worker !== 'undefined' && typeof Blob !== 'undefined') {
        const blob = new Blob([WORKER_CODE], { type: 'application/javascript' });
        workerUrl = URL.createObjectURL(blob);
        worker = new Worker(workerUrl);

        timer = setTimeout(() => {
          cleanup();
          resolve({ success: false, reason: 'timeout' });
        }, timeoutMs);

        worker.onmessage = (e) => {
          cleanup();
          resolve(e.data);
        };

        worker.onerror = () => {
          cleanup();
          // Worker 执行出错时降级：主线程用同一 SHA-256 算法求解（禁止伪哈希，否则验签必失败）
          resolve(solvePoWMainThread(salt, difficulty, maxIters));
        };

        worker.postMessage({ salt, difficulty, maxIters });
        return;
      }
    } catch {
      // Worker 实例化受限环境
    }

    // 主线程降级求解
    resolve(solvePoWMainThread(salt, difficulty, maxIters));
  });
}

/**
 * 主线程降级：Web Crypto SHA-256，与 Worker / 服务端算法一致。
 * 仅限制迭代上限，避免长时间卡死 UI。HTTPS 下 crypto.subtle 恒可用。
 */
async function solvePoWMainThread(salt, difficulty, maxIters = 500000) {
  const limit = Math.min(maxIters, 200000);
  const t0 = Date.now();
  if (typeof crypto === 'undefined' || !crypto.subtle) {
    return { success: false, reason: 'subtle_unavailable' };
  }
  try {
    const enc = new TextEncoder();
    for (let i = 0; i < limit; i += 1) {
      const nonce = String(i);
      const digest = await crypto.subtle.digest('SHA-256', enc.encode(salt + nonce));
      if (hasLeadingZeroBitsBytes(new Uint8Array(digest), difficulty)) {
        return {
          success: true,
          nonce,
          iters: i,
          durationMs: Date.now() - t0,
        };
      }
    }
  } catch {
    return { success: false, reason: 'main_thread_error' };
  }
  return { success: false, reason: 'main_thread_exhausted' };
}

function hasLeadingZeroBitsBytes(bytes, bits) {
  if (!bits || bits <= 0) return true;
  const fullBytes = Math.floor(bits / 8);
  const remBits = bits % 8;
  for (let i = 0; i < fullBytes; i += 1) {
    if (bytes[i] !== 0) return false;
  }
  if (remBits > 0) {
    const mask = 0xff << (8 - remBits);
    if ((bytes[fullBytes] & mask) !== 0) return false;
  }
  return true;
}

/**
 * 向后端请求动态 PoW 挑战题
 * @param {string} action
 * @returns {Promise<Object|null>}
 */
export async function fetchPoWChallenge(action = 'auth_login') {
  try {
    const res = await request({
      url: '/app/liyuan-auth/pow/challenge',
      method: 'get',
      params: { action },
    });
    const d = res?.data || res;
    if (d && d.challengeId && d.salt) {
      return d;
    }
  } catch (err) {
    console.warn('[PoW] fetch challenge failed:', err);
  }
  return null;
}

/**
 * 获取并求解 PoW，返回可直接注入 BotProofPayload 的 pow 结构
 * @param {string} action
 * @returns {Promise<Object|null>}
 */
export async function getPoWPayload(action = 'auth_login') {
  const chal = await fetchPoWChallenge(action);
  if (!chal) return null;

  const res = await solvePoW({
    salt: chal.salt,
    difficulty: chal.difficulty,
  });

  if (res && res.success && res.nonce) {
    return {
      cid: chal.challengeId,
      nonce: res.nonce,
      salt: chal.salt,
      dif: chal.difficulty,
      exp: chal.expireAt,
      sig: chal.signature,
    };
  }
  return null;
}
